<template>
  <div class="yt-search__loc" :class="{ 'is-open': open, 'is-has-value': model.length }" @click.stop>
    <div
      class="yt-search__location"
      :class="{ 'is-open': open }"
      role="button"
      tabindex="0"
      :aria-expanded="open"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <i class="fa-solid fa-location-dot" aria-hidden="true" />
      <span v-if="model.length" class="yt-search__loc-badge" aria-hidden="true">
        {{ model.length }}
      </span>
      <div class="yt-search__chips">
        <span
          v-for="item in selectedItems"
          :key="item.value"
          class="yt-search__chip"
          :title="item.label"
        >
          <span class="yt-search__chip-label">{{ item.label }}</span>
          <button
            type="button"
            class="yt-search__chip-remove"
            :aria-label="$t('home.remove')"
            @click.stop="remove(item.value)"
          >
            <i class="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </span>
        <span
          v-if="!selectedItems.length"
          class="yt-search__loc-placeholder"
        >
          {{ $t('home.locationPlaceholder') }}
        </span>
      </div>
    </div>

    <div
      v-if="open"
      class="yt-loc-backdrop"
      aria-hidden="true"
      @click="close"
    />

    <div v-show="open" class="yt-loc-panel" @mouseleave="scheduleCloseSub">
      <div class="yt-loc-panel__head">
        <div class="yt-loc-panel__title">
          {{ $t('home.locationTitle') }}
          <span class="yt-loc-panel__count">({{ filtered.length }})</span>
        </div>
        <button
          type="button"
          class="yt-loc-panel__close"
          :aria-label="$t('header.close')"
          @click="close"
        >
          <i class="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>

      <div class="yt-loc-panel__search">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
        <input
          v-model="query"
          type="search"
          class="yt-loc-panel__search-input"
          :placeholder="$t('home.locationSearch')"
          autocomplete="off"
        />
      </div>

      <div class="yt-loc-panel__meta">
        <span v-if="isMaxed" class="yt-loc-panel__hint">
          {{ $t('home.locationMaxSelected') }}
        </span>
        <span v-else class="yt-loc-panel__hint yt-loc-panel__hint--muted">
          {{ $t('home.locationSelectHint', { max: MAX_LOCATIONS }) }}
        </span>
        <button
          v-if="model.length"
          type="button"
          class="yt-loc-panel__clear"
          @click="clear"
        >
          {{ $t('home.locationClearAll') }}
        </button>
      </div>

      <div class="yt-loc-panel__body" :class="{ 'is-sub-open': !!activeParent }">
        <div class="yt-loc-panel__list">
          <div
            v-for="opt in filtered"
            :key="opt.value"
            class="yt-loc-panel__row"
            :class="{ 'is-active': hoveredId === opt.value }"
            @mouseenter="onRowEnter(opt)"
          >
            <button
              type="button"
              class="yt-loc-panel__item"
              :class="{
                'is-checked': isSelected(opt.value),
                'is-disabled': !isSelected(opt.value) && isMaxed,
              }"
              :disabled="!isSelected(opt.value) && isMaxed"
              @click="toggleValue(opt.value)"
            >
              <span
                class="yt-loc-panel__check"
                :class="{ 'is-checked': isSelected(opt.value) }"
                aria-hidden="true"
              >
                <i v-if="isSelected(opt.value)" class="fa-solid fa-check" />
              </span>
              <span class="yt-loc-panel__item-text">
                <span class="yt-loc-panel__item-label">{{ opt.label }}</span>
                <span v-if="opt.hasChildren" class="yt-loc-panel__item-sub">
                  {{ $t('home.locationAllDistricts') }}
                </span>
              </span>
            </button>

            <button
              v-if="opt.hasChildren"
              type="button"
              class="yt-loc-panel__submenu-btn"
              :class="{ 'is-open': hoveredId === opt.value }"
              :aria-label="opt.label"
              :aria-expanded="hoveredId === opt.value"
              @mouseenter="openSub(opt)"
              @click.stop.prevent="openSub(opt)"
            >
              <i class="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          </div>

          <div v-if="loading" class="yt-loc-panel__empty">
            <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
          </div>
          <div v-else-if="!filtered.length" class="yt-loc-panel__empty">
            {{ $t('home.noSuggest') }}
          </div>
        </div>

        <div
          v-show="activeParent"
          class="yt-loc-sub"
          @mouseenter="cancelCloseSub"
          @mouseleave="scheduleCloseSub"
        >
          <div class="yt-loc-panel__title">
            {{ activeParent?.label }}
            <span class="yt-loc-panel__count">({{ filteredChildren.length }})</span>
          </div>

          <div class="yt-loc-panel__search">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              v-model="childQuery"
              type="search"
              class="yt-loc-panel__search-input"
              :placeholder="$t('home.locationSearch')"
              autocomplete="off"
            />
          </div>

          <div class="yt-loc-panel__list yt-loc-sub__list">
            <button
              v-for="child in filteredChildren"
              :key="child.value"
              type="button"
              class="yt-loc-panel__item"
              :class="{
                'is-checked': isSelected(child.value),
                'is-disabled': !isSelected(child.value) && isMaxed,
              }"
              :disabled="!isSelected(child.value) && isMaxed"
              @click="toggleValue(child.value)"
            >
              <span
                class="yt-loc-panel__check"
                :class="{ 'is-checked': isSelected(child.value) }"
                aria-hidden="true"
              >
                <i v-if="isSelected(child.value)" class="fa-solid fa-check" />
              </span>
              <span class="yt-loc-panel__item-text">
                <span class="yt-loc-panel__item-label">{{ child.label }}</span>
              </span>
            </button>
            <div v-if="!filteredChildren.length" class="yt-loc-panel__empty">
              {{ $t('home.noSuggest') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { CONFIG_AREA_LIST } from '@/api/const/config/cache'
import { apiConfigGet } from '@/api/index'

type LocOption = {
  value: string
  label: string
  hasChildren: boolean
  children?: LocOption[]
}

const MAX_LOCATIONS = 3
const SUB_CLOSE_DELAY = 160

const model = defineModel<string[]>({ default: () => [] })
const emit = defineEmits<{ opened: []; closed: [] }>()

const open = ref(false)
const query = ref('')
const childQuery = ref('')
const loading = ref(false)
const options = ref<LocOption[]>([])
const hoveredId = ref<string | null>(null)
let subCloseTimer: ReturnType<typeof setTimeout> | null = null

const normalize = (str: string) =>
  (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')

const mapNode = (item: any): LocOption | null => {
  const value = item?.id ?? item?.code ?? item?.value
  const label = item?.name ?? item?.label
  if (value == null || !label) return null
  const rawChildren = Array.isArray(item?.children) ? item.children : []
  const children = rawChildren
    .map(mapNode)
    .filter(Boolean) as LocOption[]
  return {
    value: String(value),
    label: String(label),
    hasChildren: children.length > 0,
    children,
  }
}

const parseAreaList = (raw: unknown): LocOption[] => {
  let payload: any = raw
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload || '[]')
    } catch {
      return []
    }
  }

  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.body)
      ? payload.body
      : Array.isArray(payload?.data)
        ? payload.data
        : []

  return list.map(mapNode).filter(Boolean) as LocOption[]
}

const loadAreas = async () => {
  if (options.value.length || loading.value) return
  loading.value = true
  try {
    const res: any = await apiConfigGet(CONFIG_AREA_LIST)
    const raw = res?.data?.data ?? res?.data
    options.value = parseAreaList(raw)
  } catch {
    options.value = []
  } finally {
    loading.value = false
  }
}

const optionMap = computed(() => {
  const map = new Map<string, LocOption>()
  for (const opt of options.value) {
    map.set(opt.value, opt)
    for (const child of opt.children || []) {
      map.set(child.value, child)
    }
  }
  return map
})

const selectedItems = computed(() =>
  model.value
    .map((value) => optionMap.value.get(value))
    .filter(Boolean) as LocOption[],
)

const matchLabel = (label: string, q: string) => normalize(label).includes(q)

const provinceMatchesQuery = (opt: LocOption, q: string) => {
  if (matchLabel(opt.label, q)) return true
  return (opt.children || []).some((c) => matchLabel(c.label, q))
}

const filtered = computed(() => {
  const q = normalize(query.value.trim())
  if (!q) return options.value
  return options.value.filter((o) => provinceMatchesQuery(o, q))
})

const activeParent = computed(
  () => options.value.find((o) => o.value === hoveredId.value) || null,
)

const filteredChildren = computed(() => {
  const children = activeParent.value?.children || []
  // Prefer child panel search; fall back to main query for live cross-search
  const raw = childQuery.value.trim() || query.value.trim()
  const q = normalize(raw)
  if (!q) return children
  return children.filter((c) => matchLabel(c.label, q))
})

const isMaxed = computed(() => model.value.length >= MAX_LOCATIONS)
const isSelected = (value: string) => model.value.includes(value)

const cancelCloseSub = () => {
  if (subCloseTimer) {
    clearTimeout(subCloseTimer)
    subCloseTimer = null
  }
}

const scheduleCloseSub = () => {
  cancelCloseSub()
  subCloseTimer = setTimeout(() => {
    hoveredId.value = null
    childQuery.value = ''
  }, SUB_CLOSE_DELAY)
}

const openSub = (opt: LocOption) => {
  if (!opt.hasChildren) return
  cancelCloseSub()
  if (hoveredId.value !== opt.value) {
    childQuery.value = ''
  }
  hoveredId.value = opt.value
}

const onRowEnter = (opt: LocOption) => {
  if (hoveredId.value && opt.value !== hoveredId.value && !opt.hasChildren) {
    scheduleCloseSub()
  }
}

/** Live search: when typing matches a district, open that province submenu immediately */
const syncSubFromQuery = () => {
  const raw = query.value.trim()
  const q = normalize(raw)
  if (!q) return

  const parentHit = options.value.find((o) => matchLabel(o.label, q))
  if (parentHit) {
    // Province name match — keep submenu closed unless already browsing children
    return
  }

  const childHit = options.value.find((o) =>
    (o.children || []).some((c) => matchLabel(c.label, q)),
  )
  if (childHit) {
    cancelCloseSub()
    hoveredId.value = childHit.value
    childQuery.value = raw
  }
}

const toggle = () => {
  open.value = !open.value
  if (open.value) {
    query.value = ''
    childQuery.value = ''
    hoveredId.value = null
    emit('opened')
    loadAreas()
  } else {
    hoveredId.value = null
    childQuery.value = ''
    emit('closed')
  }
}

const close = () => {
  if (!open.value) return
  open.value = false
  query.value = ''
  childQuery.value = ''
  hoveredId.value = null
  emit('closed')
}

const toggleValue = (value: string) => {
  if (isSelected(value)) {
    model.value = model.value.filter((v) => v !== value)
    return
  }
  if (isMaxed.value) return
  model.value = [...model.value, value]
}

const clear = () => {
  model.value = []
}

const remove = (value: string) => {
  model.value = model.value.filter((v) => v !== value)
}

watch(query, () => {
  syncSubFromQuery()
  if (
    hoveredId.value &&
    !filtered.value.some((o) => o.value === hoveredId.value)
  ) {
    hoveredId.value = null
    childQuery.value = ''
  }
})

onMounted(() => {
  loadAreas()
})

onBeforeUnmount(() => {
  cancelCloseSub()
})

defineExpose({ close, open })
</script>

<style scoped>
.yt-search__loc {
  position: relative;
  flex: 0 1 auto;
  max-width: min(320px, 42%);
  min-width: 140px;
}

.yt-search__location {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  max-height: 36px;
  padding: 2px 10px;
  border: none;
  border-radius: 999px;
  background: #f2f2f2;
  color: #3c4043;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
}

.yt-search__location > .fa-location-dot {
  flex-shrink: 0;
  font-size: 15px;
  color: #5f6368;
}

.yt-search__loc-badge {
  display: none;
}

.yt-search__location:hover,
.yt-search__location.is-open {
  background: #e8e8e8;
}

.yt-search__chips {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.yt-search__loc-placeholder {
  color: #3c4043;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.yt-search__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 56px;
  max-width: 100%;
  height: 26px;
  padding: 0 8px 0 10px;
  border-radius: 999px;
  background: #e8e8e8;
  color: #3c4043;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
}

.yt-search__chip-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yt-search__chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6368;
  font-size: 10px;
  cursor: pointer;
  flex-shrink: 0;
}

.yt-search__chip-remove:hover {
  background: rgba(60, 64, 67, 0.12);
  color: #202124;
}

.yt-loc-backdrop {
  display: none;
}

.yt-loc-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 40;
  width: min(360px, calc(100vw - 24px));
  padding: 14px 12px 10px;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
}

.yt-loc-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  padding: 0 4px;
}

.yt-loc-panel__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f0f0f;
}

.yt-loc-panel__count {
  margin-left: 4px;
  font-weight: 600;
  color: #0b57d0;
  white-space: nowrap;
}

.yt-loc-panel__close {
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.yt-loc-panel__close:hover {
  background: #f2f2f2;
  color: #111;
}

.yt-loc-panel__search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  margin-bottom: 10px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #717171;
}

.yt-loc-panel__search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #0f0f0f;
}

.yt-loc-panel__search-input::placeholder {
  color: #9aa0a6;
}

.yt-loc-panel__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
  padding: 0 4px;
}

.yt-loc-panel__hint {
  font-size: 13px;
  font-weight: 500;
  color: #ff6a00;
}

.yt-loc-panel__hint--muted {
  color: #8c8c8c;
}

.yt-loc-panel__clear {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0b57d0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.yt-loc-panel__clear:hover {
  text-decoration: underline;
}

.yt-loc-panel__body {
  position: relative;
}

.yt-loc-panel__list {
  max-height: 260px;
  overflow-y: auto;
  padding-right: 2px;
}

.yt-loc-panel__row {
  display: flex;
  align-items: stretch;
  gap: 2px;
  border-radius: 8px;
}

.yt-loc-panel__row.is-active,
.yt-loc-panel__row:hover {
  background: #f8f9fa;
}

.yt-loc-panel__item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  min-height: 48px;
  padding: 8px 6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #0f0f0f;
  text-align: left;
  cursor: pointer;
}

.yt-loc-panel__item:hover:not(:disabled) {
  background: transparent;
}

.yt-loc-panel__item.is-disabled,
.yt-loc-panel__item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.yt-loc-panel__submenu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  flex-shrink: 0;
  margin: 4px 2px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #9aa0a6;
  font-size: 12px;
  cursor: pointer;
}

.yt-loc-panel__submenu-btn:hover,
.yt-loc-panel__submenu-btn.is-open {
  background: #eef2f7;
  color: #0b57d0;
}

.yt-loc-panel__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border: 1.5px solid #c4c7c5;
  border-radius: 3px;
  background: #fff;
  color: #fff;
  font-size: 11px;
}

.yt-loc-panel__check.is-checked {
  border-color: #0b57d0;
  background: #0b57d0;
}

.yt-loc-panel__item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.yt-loc-panel__item-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
}

.yt-loc-panel__item-sub {
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.2;
}

.yt-loc-panel__empty {
  padding: 18px 8px;
  text-align: center;
  color: #8c8c8c;
  font-size: 13px;
}

.yt-loc-sub {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  right: auto;
  z-index: 41;
  width: min(360px, calc(100vw - 24px));
  padding: 14px 12px 10px;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
}

.yt-loc-sub__list {
  max-height: 300px;
  overflow-y: auto;
}

@media (max-width: 900px) {
  .yt-search__loc {
    display: block;
    max-width: none;
    min-width: 0;
  }

  .yt-search__chips,
  .yt-search__loc-placeholder {
    display: none;
  }

  .yt-search__location {
    position: relative;
    width: 36px;
    min-height: 36px;
    padding: 0;
    justify-content: center;
    gap: 0;
  }

  .yt-search__loc-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 999px;
    background: #ff6a00;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
  }

  .yt-loc-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1190;
    background: rgba(0, 0, 0, 0.35);
  }

  .yt-loc-panel {
    position: fixed;
    top: auto;
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: auto;
    max-height: min(92vh, 720px);
    height: min(92vh, 720px);
    display: flex;
    flex-direction: column;
    padding-bottom: 12px;
    border-radius: 16px;
    z-index: 1200;
    overflow: hidden;
  }

  .yt-loc-panel__close {
    display: inline-flex;
  }

  .yt-loc-panel__body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
  }

  /* Province list */
  .yt-loc-panel__body > .yt-loc-panel__list {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  /* When submenu open: shrink province list, give children most of the sheet */
  .yt-loc-panel__body.is-sub-open > .yt-loc-panel__list {
    flex: 0 0 auto;
    max-height: 22%;
  }

  .yt-loc-sub {
    position: static;
    left: auto;
    right: auto;
    width: 100%;
    margin-top: 0;
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: none;
    border-radius: 12px;
    background: #f8f9fb;
    padding: 10px 8px 8px;
  }

  .yt-loc-panel__body.is-sub-open .yt-loc-sub {
    flex: 1 1 78%;
    min-height: 62%;
  }

  .yt-loc-sub .yt-loc-panel__title {
    font-size: 15px;
    margin-bottom: 8px;
  }

  .yt-loc-sub .yt-loc-panel__search {
    margin-bottom: 8px;
    flex-shrink: 0;
  }

  .yt-loc-sub__list {
    flex: 1 1 auto;
    min-height: 180px;
    max-height: none !important;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .yt-loc-panel__submenu-btn {
    width: 40px;
  }
}
</style>
